import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/utils/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds hover elevation + pointer affordance for clickable cards. */
  interactive?: boolean;
}

export function Card({ className, interactive = false, ...props }: CardProps) {
  return (
    <div
      className={cn(
        // The one card shell: hairline `border` (#eaecf0, the structural
        // separator) on a `bg-card` surface with `shadow-xs`. It was
        // border-outline + shadow-none, which is the stronger control edge
        // with no elevation at all — cards read as flat panels cut into the
        // page instead of surfaces sitting on it.
        'rounded-card border border-border bg-card shadow-xs transition-shadow duration-200 ease-standard',
        interactive &&
          'cursor-pointer hover:border-primary/30 hover:shadow-surface-1 active:translate-y-0',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
  children,
}: {
  /** Optional so the compositional form `<CardHeader><CardTitle/></CardHeader>` works. */
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  // One 16px gutter across header / body / footer so the card edge lines up
  // with the table cells inside it (p-5 header + p-4 body did not).
  return (
    <div className={cn('flex items-start justify-between gap-3 border-b border-border px-4 py-3.5', className)}>
      <div className="min-w-0">
        {title !== undefined && <h3 className="text-data-lg font-semibold text-on-surface">{title}</h3>}
        {subtitle && <p className="mt-0.5 text-body-sm text-on-surface-variant">{subtitle}</p>}
        {children}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4', className)} {...props} />;
}

// ─── Compositional primitives ────────────────────────────────────────────────
// The Services & Projects pages were authored against the shadcn-style Card API
// (<CardHeader><CardTitle/></CardHeader><CardContent/>). These wrappers render
// the same visual shell as CardHeader/CardBody above, so both spellings work
// and existing callers are untouched.

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-data-lg font-semibold text-on-surface', className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('mt-0.5 text-body-sm text-on-surface-variant', className)} {...props} />;
}

/** Alias of CardBody, for pages using the shadcn naming. */
export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-t border-border px-4 py-3.5', className)} {...props} />;
}
